import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../../../../core/common/dataTable/index";
import Cookies from "js-cookie";
import Spin from "antd/es/spin";
import { useUserPermissions } from "../../../../UserPermissionsContext";
import { all_routes } from "../../../../router/all_routes";
import { CloseCircleOutlined } from "@ant-design/icons";
import { Select } from "antd";

type Permission = { id: number; name: string };
type Admin = {
  permissions: Array<number>;
  name: string | null;
  id: number;
};

const SEDTeamManagement = () => {
  const { Option } = Select;
  const routes = all_routes;
  const navigate = useNavigate();
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [admin, setAdmin] = useState<Admin[]>([]);
  const [totalCount, setTotalCount] = useState();
  const [teamLeadFilter, setTeamLeadFilter] = useState<any[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("teamManagementCurrentPage");
    return savedPage ? JSON.parse(savedPage) : 1;
  });
  useEffect(() => {
    localStorage.setItem(
      "teamManagementCurrentPage",
      JSON.stringify(currentPage),
    );
  }, [currentPage]);
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem("SEDTeamManagement_filters");
    return savedFilters
      ? JSON.parse(savedFilters)
      : {
          name: "",
          team_lead: "",
        };
  });
  useEffect(() => {
    localStorage.setItem("SEDTeamManagement_filters", JSON.stringify(filters));
  }, [filters]);
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");

  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject,
    );
  };

  useEffect(() => {
    if (!checkPermission("view_sedmanagers", "sedmanagers")) {
      Cookies.remove("authToken");
      navigate(routes.crmLandingPage);
    } else {
      fetchData(currentPage, pageSize, searchText);
    }
  }, [userPermissions, currentPage, appliedFilters]);

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  useEffect(() => {
    const fetchMediums = async () => {
      try {
        const response = await fetch(
          `${webApi}sed/sed_team_lead_dropdown_for_list_filter/`,
          options,
        );
        const data = await response.json();
        setTeamLeadFilter(data);
      } catch (error) {
        console.error("Failed to fetch medium", error);
      }
    };

    fetchMediums();
  }, []);

  //  Fetch the agent Reports
  const fetchData = async (
    pageNumber: number,
    pageSize: number,
    searchText: string,
  ) => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageNumber.toString(),
        page_size: pageSize.toString(),
        search: searchText || "",
        name: appliedFilters.name || "",
        team_lead: appliedFilters.team_lead || "",
      });
      const res = await fetch(
        `${webApi}sed/sed_team_mgmt/?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        },
      );
      if (!res.ok) {
        const errorData = await res.json();
        if (errorData.code === "token_not_valid") {
          navigate("/academic/sri-chaitanya/crm/landing-page");
        }
        throw new Error(
          errorData.detail || "Failed to fetch Team manager data",
        );
      }
      const json = await res.json();
      setAdmin(json.results);
      setCurrentPage(json.current_page_number);
      setTotalCount(json.total_count);
      setPageSize(json.default_page_size);
    } catch (error) {
      console.log("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
    fetchData(current, pageSize, searchText); // Fetch new data based on the updated pagination
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchData(currentPage, pageSize, value);
  };

  const handleApplyClick = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setShowFilter(false);
  };

  const handleResetClick = () => {
    const emptyFilters = {
      name: "",
      team_lead: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
    setShowFilter(false);
  };

  //Reusable function to display applied filters
  const renderFilterTag = (
    value: string,
    filterKey: keyof typeof appliedFilters,
  ) => {
    if (!value) return null;

    return (
      <span
        style={{
          marginLeft: 8,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#1890ff", marginRight: 4 }}>({value})</span>

        <CloseCircleOutlined
          onClick={() => {
            const updatedFilters = {
              ...filters,
              [filterKey]: "",
            };

            setFilters(updatedFilters);
            setAppliedFilters(updatedFilters);
          }}
          style={{ color: "red", cursor: "pointer" }}
        />
      </span>
    );
  };

  const columns = [
    {
      title: (
        <span>
          Team Name
          {renderFilterTag(appliedFilters.name, "name")}
        </span>
      ),
      dataIndex: "name",
    },
    {
      title: (
        <span>
          Team Lead
          {renderFilterTag(
            teamLeadFilter.find(
              (x) => String(x.id) === String(appliedFilters.team_lead),
            )?.name || "",
            "team_lead",
          )}
        </span>
      ),
      dataIndex: "team_lead_name",
    },
    ...(checkPermission("change_sedteammgmt", "sedteammgmt")
      ? [
          {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (_text: any, record: { id: any }) => (
              <div className="d-flex align-items-center">
                <Link
                  className="btn btn-icon btn-sm btn-soft-info rounded-pill"
                  to={routes.SEDEditTeamManagement}
                  state={{ id: record.id }}
                >
                  <i className="feather-edit" />
                </Link>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">SED Teams</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.SEDDashBoard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    SED Teams
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                {checkPermission("add_sedteammgmt", "sedteammgmt") && (
                  <Link
                    to={routes.SEDAddTeamManagement}
                    className="btn btn-primary"
                  >
                    <i className="ti ti-square-rounded-plus-filled me-2" />
                    Add Team Member
                  </Link>
                )}
              </div>
            </div>
          </div>
          {/* /Page Header */}
          {/* Guardians List */}
          <div
            className="card "
            style={{
              width: "100%",
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "10px",
            }}
          >
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">SED Teams</h4>
              <div className="d-flex align-items-center flex-wrap"></div>
              <div className="dropdown mb-2 me-2 position-relative d-inline-block">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  Filter
                </button>

                {showFilter && (
                  <div
                    className="card mt-3 shadow-sm"
                    style={{
                      width: "500px",
                      position: "absolute",
                      right: 0,
                      zIndex: 1000,
                      borderRadius: "12px",
                    }}
                  >
                    <form>
                      {/* Header */}
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4 className="mb-0 me-auto">Filter</h4>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowFilter(false)}
                        />
                      </div>

                      {/* Body */}
                      <div className="p-3">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Name</label>
                              <input
                                type="text"
                                className="form-control"
                                value={filters.name}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    name: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Team Lead</label>
                              <Select
                                showSearch
                                allowClear
                                placeholder="Select Team Lead"
                                value={filters.team_lead || ""}
                                onChange={(value) =>
                                  setFilters({
                                    ...filters,
                                    team_lead: value || "",
                                  })
                                }
                                style={{ width: "100%" }}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  (option?.children as unknown as string)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              >
                                {teamLeadFilter.map((teamLead) => (
                                  <Option key={teamLead.id} value={teamLead.id}>
                                    {teamLead.name}
                                  </Option>
                                ))}
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="p-3 border-top d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light me-3"
                          onClick={handleResetClick}
                        >
                          Reset
                        </button>

                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {/* <div
                className="serch-global text-right"
                style={{ marginRight: "15px" }}
              >
                <input
                  type="search"
                  className="form-control form-control-sm mb-3 w-auto float-end"
                  value={searchText}
                  placeholder="Search"
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-controls="DataTables_Table_0"
                ></input>
              </div> */}
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: "100vh",
                    paddingTop: "20px",
                  }}
                >
                  <Spin /> {/* Display loader while loading */}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={admin}
                  Selection={false}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    locale: { items_per_page: "" },
                    nextIcon: <span>Next</span>,
                    prevIcon: <span>Prev</span>,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    total: totalCount,
                    showTotal: (total: any, range: any[]) =>
                      `${range[0]}-${range[1]} of ${total}`,
                  }}
                  onChange={handleTableChange} // Move this outside of pagination
                  onSearch={handleSearch}
                  searchText={searchText}
                />
              )}
            </div>
          </div>
          {/* /Guardians List */}
        </div>
      </div>
      <>
        {/* Delete Modal */}
        {/* <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete all the marked items, this cant be undone
                    once you delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      data-bs-dismiss="modal"
                      className="btn btn-danger"
                    >
                      Yes, Delete
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div> */}
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default SEDTeamManagement;
